import { Dates } from "src/modules/utils/shared/dates";
import { Strings } from "src/modules/utils/shared/strings";
import { Injectable, inject } from '@angular/core';
import { MenuItem } from '@openng/optimus-ui/api';
import { MainAppService } from 'src/modules/app-template/services/main-app.service';
import { FrontendField } from 'src/modules/sm-base/netgen/FrontendField';
import { FrontendFieldError } from 'src/modules/sm-base/netgen/FrontendFieldError';
import { FrontendFieldListItem } from 'src/modules/sm-base/netgen/FrontendFieldListItem';
import { FrontendFieldType } from 'src/modules/sm-base/netgen/FrontendFieldType';
import { FrontendForm } from 'src/modules/sm-base/netgen/FrontendForm';
import { RestEndpoint } from 'src/modules/sm-base/models/rest-endpoint.model';
import { Utils } from 'src/modules/utils/shared/utils';
import { ClassRegisterSettings } from '../shared/class-register-settings.entity';
import { ClassRegister } from '../shared/class-register.entity';
import { EnaioCertificatesTools } from '../shared/enaio-certificates-tools';
import { Pupil } from '../shared/pupil.model';
import { SchoolClass } from '../shared/school-class.model';
import { SchoolType } from '../shared/school-type.enum';
import { Teacher } from '../shared/teacher.model';
import { TimedCacheMap } from 'src/modules/sm-base/models/timed-cache-map.model';
import { Arrays } from 'src/modules/utils/shared/arrays';
import { Objects } from "src/modules/utils/shared/objects";

@Injectable({
    providedIn: 'root'
})
export class EnaioCertificateService {

    app = inject(MainAppService);

    private classRegisterTimedCache = new TimedCacheMap<number, ClassRegister>(60 * 60 * 1000, async id => RestEndpoint.main().findById(id).run("api/cer/classregister/schoolclass").get(ClassRegister, true));
    private schoolClassTimedCache = new TimedCacheMap<number, SchoolClass>(60 * 60 * 1000, async id => RestEndpoint.main().findById(id).run("api/cer/schoolclass").get(SchoolClass));

    isAdminForCertificates(): boolean {
        return EnaioCertificatesTools.isAdminForCertificates(this.app.auth.userDetails);
    }

    isCertificateEditor(): boolean {
        return EnaioCertificatesTools.isCertificateEditor(this.app.auth.userDetails);
    }

    isFinalExamEditor(settings: ClassRegisterSettings): boolean {
        return this.isCertificateEditor() || this.isSecretary() || settings.isGradeLevelCoordinator(this.app.getUserName());
    }

    isClassRegisterEditor(): boolean {
        return EnaioCertificatesTools.isClassRegisterEditor(this.app.auth.userDetails);
    }

    isSecretary(): boolean {
        return EnaioCertificatesTools.isSecretary(this.app.auth.userDetails);
    }

    isPupilListEditor(settings: ClassRegisterSettings): boolean {
        return this.isSecretary() || settings.isGradeLevelCoordinator(this.app.getUserName());
    }

    getHomeClassRegister(): MenuItem {
        return { label: "Digitales Klassenbuch", routerLink: ["/enaio-certificates", "class-registers", "home"] };
    }

    getHomeCertificates(): MenuItem {
        return { label: "Zeugnisse", routerLink: ["/enaio-certificates", "home"] };
    }

    getHomeCovidTests(): MenuItem {
        return { label: "Corona Testungen", routerLink: ["/enaio-certificates", "covid-tests", "home"] };
    }

    getHomeCourses(): MenuItem {
        return { label: "Kursbuch", routerLink: ["/enaio-certificates", "class-registers", "courses"] };
    }

    getHomeGradeBook(): MenuItem {
        return { label: "Notenbuch", routerLink: ["/enaio-certificates", "grade-book", "home"] };
    }

    async updateNavigationClassRegister(classRegisterId: number, additionalMenuItems: MenuItem[]): Promise<void> {
        this.app.updateNavigation(null, [
            this.getHomeClassRegister(),
            { label: "Klasse " + await this.restGetClassRegisterName(classRegisterId), routerLink: ["/enaio-certificates", "class-registers", "class-register", classRegisterId] },
            ...additionalMenuItems
        ]);
    }

    getSickNoteForm(pupils: Pupil[], allowUnexcused: boolean, reasonDropdown: boolean): FrontendForm {
        let result = new FrontendForm([
            pupils != null ? new FrontendField("pupilId", "Schüler", FrontendFieldType.dropdown, { 
                mandatory: true, 
                dropdownAutoComplete: true, 
                listItems: FrontendFieldListItem.sort(pupils.map(p => new FrontendFieldListItem(p.id, p.getFullName())))
            }) : null,
            new FrontendField("fromDate", "Von", FrontendFieldType.date, {
                value: new Date(), 
                mandatory: true, 
                guiGroup: "fromDate", 
                guiWidthInGroup: 50
            }),
            new FrontendField("fromHour", "Stunde", FrontendFieldType.dropdown, {
                value: -1, 
                mandatory: true, 
                minValue: -1, 
                guiGroup: "fromDate", 
                guiWidthInGroup: 50, 
                guiGroupOwnLabel: false,
                listItems: [new FrontendFieldListItem(-1, "Ganzer Tag"), ...Utils.getRange(0, EnaioCertificatesTools.getMaxHoursPerDay() - 1).map(hour => new FrontendFieldListItem(hour, "ab " + (hour + 1) + ". Stunde"))]
            }),
            new FrontendField("toDate", "Bis", FrontendFieldType.date, {
                value: new Date(), 
                mandatory: true, 
                guiGroup: "toDate", 
                guiWidthInGroup: 50
            }),
            new FrontendField("toHour", "Stunde", FrontendFieldType.dropdown, {
                value: -1, 
                mandatory: true, 
                minValue: -1, 
                guiGroup: "toDate", 
                guiWidthInGroup: 50, 
                guiGroupOwnLabel: false,
                listItems: [new FrontendFieldListItem(-1, "Ganzer Tag"), ...Utils.getRange(0, EnaioCertificatesTools.getMaxHoursPerDay() - 1).map(hour => new FrontendFieldListItem(hour, "bis " + (hour + 1) + ". Stunde"))]
            }),
            new FrontendField("reason", "Grund der Abmeldung", reasonDropdown ? FrontendFieldType.dropdown : FrontendFieldType.string, { 
                mandatory: true, 
                listItems: FrontendFieldListItem.make(["krank", "Arzttermin", "sonstiger Termin"])
            }),
            allowUnexcused ? new FrontendField("excused", "Entschuldigt", FrontendFieldType.bool, { value: true }) : null
        ]);
        result.additionalValidation = form => form.getValueDate("fromDate") != null && form.getValueDate("toDate") != null && Dates.less(form.getValueDate("toDate"), form.getValueDate("fromDate"))
            ? [new FrontendFieldError("toDate", "Das Enddatum darf nicht vor dem Startdatum liegen")]
            : [];
        return result;
    }

    async restGetDomains(grade?: number, schoolType?: SchoolType, addEmpty?: boolean, addSpecialForTimeTable?: ClassRegisterSettings, applySettingsRestrictions?: ClassRegisterSettings): Promise<string[]> {
        let fromEnaio = await RestEndpoint.main().query(Objects.removeNullValues({ grade, schoolType })).run("api/cer/competency/domains").listStrings();
        if (applySettingsRestrictions != null) {
            fromEnaio = fromEnaio.filter(domain => applySettingsRestrictions.allowedDomains.includes(domain));
        }
        return Arrays.sort([
            ...addEmpty ? [""] : [],
            ...fromEnaio,
            ...addSpecialForTimeTable != null ? EnaioCertificatesTools.getSpecialDomainsForTimeTable(addSpecialForTimeTable) : []
        ]);
    }

    async restGetFinalExamDomains(allowedDomains: string[]): Promise<string[]> {
        let result = (await this.restGetDomains(EnaioCertificatesTools.finalExamGrade)).filter(domain => allowedDomains.includes(domain));
        //Astronomie wird nur in Klasse 9 unterrichtet, muss aber auf dem Endzeugnis in Klasse 10 erscheinen
        result.push("Astronomie");
        //Deutsch/English/Mathematik mit verpflichtender schriftlicher Prüfung stehen vorne
        result = Arrays.sortBy(Arrays.getUnique(result), d => d == "Deutsch" ? "a0" : d == "Englisch" ? "a1" : d == "Mathematik" ? "a2" : d == "Astronomie" ? "zzz" : d);
        return result;
    }

    async restGetTeachers(schoolClassId?: number): Promise<Teacher[]> {
        return Arrays.sort(await RestEndpoint.main().query(Objects.removeNullValues({schoolClassId})).run("api/cer/classregister/teachers").list(Teacher));
    }

    async restGetPupil(id: number): Promise<Pupil> {
        return RestEndpoint.main().findById(id).run("api/cer/pupil").get(Pupil);
    }

    async restGetPupils(schoolClassId: number, onlyActive = true): Promise<Pupil[]> {
        return Arrays.sortBy(await RestEndpoint.main().query({schoolClassId, onlyActive}).run("api/cer/pupil").list(Pupil), p => p.lastName);
    }

    async restGetPupilsForYear(yearString: string): Promise<Pupil[]> {
        return Arrays.sortBy(await RestEndpoint.main().query({schoolYear: yearString}).run("api/cer/pupil").list(Pupil), p => p.lastName);
    }

    async restGetSchoolClasses(yearString: string): Promise<SchoolClass[]> {
        return Arrays.sort(await RestEndpoint.main().query({ year: yearString }).run("api/cer/schoolclass").list(SchoolClass));
    }

    async restGetSchoolClass(id: number): Promise<SchoolClass> {
        return this.schoolClassTimedCache.get(id);
    }

    async restGetClassRegisterSettings(year: number, isHalfYear: boolean): Promise<ClassRegisterSettings> {
        return RestEndpoint.main().query({ year, isHalfYear }).run("api/cer/classregister/settings").get(ClassRegisterSettings);
    }

    async restGetClassRegisterBasic(id: number, forceFetch?: boolean): Promise<ClassRegister> {
        return this.classRegisterTimedCache.get(id, forceFetch);
    }

    async restGetClassRegister(id: number, relations?: ("history" | "duties" | "instructions" | "teachers" | "holidays" | "fieldTrips" | "timetableEntries")[], lessonsDate?: Date, getEnaioSchoolClass?: boolean): Promise<ClassRegister> {
        let result = await RestEndpoint.main().query(Objects.removeNullValues({ id, lessons: lessonsDate != null ? Dates.format(lessonsDate, "yyyymmdd") : null,
            history: relations?.includes("history"),
            duties: relations?.includes("duties"),
            instructions: relations?.includes("instructions"),
            teachers: relations?.includes("teachers"),
            holidays: relations?.includes("holidays"),
            fieldTrips: relations?.includes("fieldTrips"),
            timetableEntries: relations?.includes("timetableEntries") })).run("api/cer/classregister/schoolclass").get(ClassRegister, true);

        if (result != null && getEnaioSchoolClass) {
            result.enaioSchoolClass = await this.restGetSchoolClass(result.schoolClassId);
        }

        return result;
    }

    async classRegisterExists(id: number): Promise<boolean> {
        return await this.restGetClassRegister(id) != null;
    }

    async restGetClassRegisterName(id: number): Promise<string> {
        let classRegister = await this.classRegisterTimedCache.get(id);
        if (classRegister == null) {
            return "";
        }
        return (await this.schoolClassTimedCache.get(classRegister.schoolClassId))?.getDisplayName() ?? "";
    }

    async restGetLessonId(classRegisterId: number, date: Date, hour: number, domain: string, courseId?: number, create = true, copyId = 0): Promise<number> {
        return RestEndpoint.main().query(Objects.removeNullValues({ classId: classRegisterId, date: Dates.format(date, "yyyymmdd"), hour, domain, courseId, create, copyId })).run("api/cer/classregister/lessonid").getNumber();
    }

}